import React from 'react';
import PropTypes from 'prop-types';
import { Box, Text } from 'grommet';
import { useAppTheme } from '@Components/Context';
import { AppButton, SimpleModal } from '@Components/Control';
import { buildDateTimeLabel } from '@Helpers/util';
import { fetchAuditPdf } from '@Actions/creatorSatellite/auditActions';
import { fmt, StatusBadge } from './YtAuditsShared';

function KpiField({ label, value, page }) {
  return (
    <Box direction="column" gap="0.25rem" flex>
      <Text size="0.75rem" weight={600} color={page.secondaryText} style={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {label}
      </Text>
      <Text size="1.1rem" weight={700} color={page.primaryText}>
        {value}
      </Text>
    </Box>
  );
}

KpiField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.node]).isRequired,
  page: PropTypes.object.isRequired,
};

const MATCH_HEADER = [
  { display: 'Video Title', uid: 'title', size: '26%' },
  { display: 'Views', uid: 'views', size: '15%' },
  { display: 'Confidence', uid: 'confidence', size: '13%' },
  { display: 'Match Type', uid: 'matchType', size: '16%' },
  { display: 'Original Video', uid: 'original', size: '28%' },
];

function MatchesTable({ matches = null, page }) {
  if (!matches || matches.length === 0) {
    return (
      <Box pad={{ vertical: '0.5rem', horizontal: '0.5rem' }}>
        <Text size="0.82rem" color={page.secondaryText}>No matches recorded.</Text>
      </Box>
    );
  }

  return (
    <Box direction="column" margin={{ top: '0.5rem' }}>
      <Box
        direction="row"
        border={{
          color: page.tableBorder, size: '1px', style: 'solid', side: 'bottom',
        }}
        pad={{ vertical: '0.5rem', horizontal: '0.75rem' }}
        gap="0.5rem"
      >
        {MATCH_HEADER.map(({ display, uid, size }) => (
          <Box key={uid} width={size}>
            <Text size="0.72rem" weight={600} color={page.tableHeader} style={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              {display}
            </Text>
          </Box>
        ))}
      </Box>
      {matches.map((m, idx) => (
        <Box
          // eslint-disable-next-line react/no-array-index-key
          key={m.videoId || idx}
          direction="row"
          align="center"
          border={{
            color: page.tableBorder, size: '1px', style: 'solid', side: 'bottom',
          }}
          pad={{ vertical: '0.65rem', horizontal: '0.75rem' }}
        >
          <Box width="26%" style={{ overflow: 'hidden' }}>
            {m.url ? (
              <a href={m.url} target="_blank" rel="noreferrer" style={{ color: page.urlColor || page.highlight, fontSize: '0.85rem', wordBreak: 'break-word' }}>
                {m.title || m.videoId || '—'}
              </a>
            ) : (
              <Text size="0.85rem" color={page.tableText}>{m.title || m.videoId || '—'}</Text>
            )}
          </Box>
          <Box width="15%" style={{ overflow: 'hidden' }}>
            <Text size="0.85rem" color={page.tableText}>{fmt(m.viewCount)}</Text>
          </Box>
          <Box width="13%" style={{ overflow: 'hidden' }}>
            <Text size="0.85rem" color={page.tableText}>
              {m.confidenceLevel || (m.confidence != null ? `${Math.round(m.confidence * 100)}%` : '—')}
            </Text>
          </Box>
          <Box width="16%" style={{ overflow: 'hidden' }}>
            <Text size="0.85rem" color={page.tableText} style={{ textTransform: 'capitalize' }}>
              {m.matchType || '—'}
            </Text>
          </Box>
          <Box width="28%" style={{ overflow: 'hidden' }}>
            <Text size="0.85rem" color={page.tableText} style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>
              {m.originalTitle || m.originalVideoId || '—'}
            </Text>
          </Box>
        </Box>
      ))}
    </Box>
  );
}

MatchesTable.propTypes = {
  matches: PropTypes.array,
  page: PropTypes.object.isRequired,
};
function InfringingChannelRow({
  vc, idx, page, onRemove, removing,
}) {
  const [expanded, setExpanded] = React.useState(false);
  const toggleExpand = React.useCallback(() => setExpanded((v) => !v), []);

  const rowBg = idx % 2 === 0 ? page.tableBg : 'rgba(255,255,255,0.12)';

  return (
    <Box
      direction="column"
      background={rowBg}
      border={{
        color: page.tableBorder, size: '1px', style: 'solid',
      }}
      style={{ flexShrink: 0, borderRadius: 'var(--app-radius)' }}
      // eslint-disable-next-line react/no-array-index-key
      key={vc.channelId || idx}
    >
      <Box
        direction="row"
        align="center"
        justify="between"
        pad={{ vertical: '0.85rem', horizontal: '0.75rem' }}
        style={{ cursor: 'pointer' }}
        onClick={toggleExpand}
      >
        <Box direction="row" align="center" gap="0.75rem" flex>
          <Text size="0.9rem" weight={500} color={page.tableText}>
            {vc.channelName || vc.channelId || '—'}
          </Text>
          {vc.channelUrl && (
            <a
              href={vc.channelUrl}
              target="_blank"
              rel="noreferrer"
              style={{ color: page.urlColor || page.highlight, fontSize: '0.78rem' }}
              onClick={(e) => e.stopPropagation()}
            >
              {vc.channelUrl}
            </a>
          )}
        </Box>
        <Box direction="row" gap="1rem" align="center" flex={false}>
          <Text size="0.82rem" color={page.secondaryText}>
            {fmt(vc.reuploadsFound ?? vc.reuploadCount)}
            {' reuploads'}
          </Text>
          <Text size="0.82rem" color={page.secondaryText}>
            {fmt(vc.totalViews)}
            {' views'}
          </Text>
          {onRemove && vc.channelId && (
            <button
              type="button"
              disabled={removing}
              onClick={(e) => {
                e.stopPropagation();
                onRemove(vc.channelId);
              }}
              style={{
                border: `0.5px solid ${page.tableBorder}`,
                borderRadius: 'var(--app-radius)',
                background: 'transparent',
                color: '#e53e3e',
                fontSize: '0.75rem',
                padding: '2px 8px',
                cursor: removing ? 'not-allowed' : 'pointer',
                opacity: removing ? 0.5 : 1,
                whiteSpace: 'nowrap',
                lineHeight: '1.4',
              }}
            >
              Remove
            </button>
          )}
          <Text size="0.82rem" color={page.highlight}>
            {expanded ? '▲' : '▼'}
          </Text>
        </Box>
      </Box>
      {expanded && (
        <Box pad={{ horizontal: '0.75rem', bottom: '1rem' }}>
          <MatchesTable matches={vc.matches} page={page} />
        </Box>
      )}
    </Box>
  );
}

InfringingChannelRow.propTypes = {
  vc: PropTypes.object.isRequired,
  idx: PropTypes.number.isRequired,
  page: PropTypes.object.isRequired,
  onRemove: PropTypes.func,
  removing: PropTypes.bool,
};
InfringingChannelRow.defaultProps = {
  onRemove: null,
  removing: false,
};

function AdminYtAuditDetailModal({
  small, audit = null, onClose, onRemoveChannel,
}) {
  const { theme: { page, button } } = useAppTheme();
  const [downloading, setDownloading] = React.useState(false);
  const [removing, setRemoving] = React.useState(false);

  const handleRemoveChannel = React.useCallback(async (channelId) => {
    if (!audit?.auditId || !onRemoveChannel) return;
    setRemoving(true);
    try {
      await onRemoveChannel(audit.auditId, channelId);
    } finally {
      setRemoving(false);
    }
  }, [audit, onRemoveChannel]);

  if (!audit) return null;

  const {
    auditId,
    channelUrl,
    channelName,
    status,
    progress,
    summary,
    verifiedChannels = [],
    createdAt,
    adminUserEmail,
  } = audit;

  const reuploadsFound = summary?.totalReuploadsFound ?? null;
  const infringingChannels = summary?.uniqueInfringingChannels ?? null;
  const revenueLostLow = summary?.estimatedRevenueLostLow ?? null;
  const revenueLostHigh = summary?.estimatedRevenueLostHigh ?? null;
  const recoveryLow = summary?.estimatedMonthlyRecoveryLow ?? null;
  const recoveryHigh = summary?.estimatedMonthlyRecoveryHigh ?? null;

  const handleDownloadPdf = React.useCallback(async () => {
    if (!auditId) return;
    setDownloading(true);
    try {
      await fetchAuditPdf(auditId);
    } finally {
      setDownloading(false);
    }
  }, [auditId]);

  const revenueLostLabel = (() => {
    if (revenueLostLow != null && revenueLostHigh != null) return `$${fmt(revenueLostLow)} – $${fmt(revenueLostHigh)}`;
    if (revenueLostHigh != null) return `$${fmt(revenueLostHigh)}`;
    if (revenueLostLow != null) return `$${fmt(revenueLostLow)}`;
    return '—';
  })();

  const recoveryLabel = (() => {
    if (recoveryLow != null && recoveryHigh != null) return `$${fmt(recoveryLow)} – $${fmt(recoveryHigh)}`;
    if (recoveryHigh != null) return `$${fmt(recoveryHigh)}`;
    if (recoveryLow != null) return `$${fmt(recoveryLow)}`;
    return '—';
  })();

  return (
    <SimpleModal
      small={small}
      toggleModal={(open) => { if (!open) onClose(); }}
      modalTitle={channelName || channelUrl || 'Audit Detail'}
      textColor={page.primaryText}
      highlightColor={page.highlight}
      modalBg={page.modalBg}
      hideLowerSection
      desktopMinWidth="58rem"
    >
      <Box
        direction="column"
        gap="1.25rem"
        pad={{ vertical: '1rem' }}
        style={{
          position: 'relative', maxHeight: '78vh', overflowY: 'auto', overflowX: 'hidden',
        }}
      >
        {removing && (
          <Box
            style={{
              position: 'absolute',
              inset: 0,
              zIndex: 10,
              background: 'rgba(0,0,0,0.35)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 'var(--app-radius)',
            }}
          >
            <Box
              pad="1rem 1.5rem"
              background={page.modalBg}
              round="var(--app-radius)"
              border={{ color: page.tableBorder, size: '1px', style: 'solid' }}
              align="center"
              gap="0.5rem"
            >
              <Text size="0.9rem" weight={500} color={page.primaryText}>Removing channel…</Text>
              <Text size="0.78rem" color={page.secondaryText}>Recalculating audit stats</Text>
            </Box>
          </Box>
        )}
        {/* Status row */}
        <Box direction="row" align="center" gap="0.75rem" wrap>
          <StatusBadge status={status} />
          {status === 'running' && progress != null && (
            <Text size="0.82rem" color={page.secondaryText}>
              {progress}
              % complete
            </Text>
          )}
          {adminUserEmail && (
            <Text size="0.82rem" color={page.secondaryText}>
              Submitted by
              {' '}
              {adminUserEmail}
            </Text>
          )}
          {createdAt && (
            <Text size="0.82rem" color={page.secondaryText}>
              {buildDateTimeLabel(createdAt)}
            </Text>
          )}
        </Box>

        {/* KPIs */}
        <Box
          direction="row"
          gap="1.5rem"
          pad="1.25rem"
          border={{ color: page.tableBorder, size: '1px', style: 'solid' }}
          round="var(--app-radius)"
          background={page.tableBg}
          wrap
        >
          <KpiField label="Est. Revenue Lost" value={revenueLostLabel} page={page} />
          <KpiField label="Monthly Recovery" value={recoveryLabel} page={page} />
          <KpiField label="Reuploads Found" value={fmt(reuploadsFound)} page={page} />
          <KpiField label="Infringing Channels" value={fmt(infringingChannels)} page={page} />
        </Box>

        {/* Infringing channels list */}
        <Box direction="column" gap="0.6rem">
          <Text size="0.9rem" weight={600} color={page.primaryText}>
            Infringing Channels
          </Text>
          <Box
            background={page.tableBg}
            style={{
              maxHeight: '26rem',
              overflowY: 'auto',
              overflowX: 'hidden',
              border: `1px solid ${page.tableBorder}`,
              borderRadius: 'var(--app-radius)',
            }}
          >
            {verifiedChannels.length === 0 ? (
              <Box pad="1.5rem" align="center">
                <Text size="0.85rem" color={page.secondaryText}>
                  {status === 'completed' ? 'No infringing channels found.' : 'Audit is still in progress.'}
                </Text>
              </Box>
            ) : (
              <Box direction="column" gap="0.4rem" pad="0.4rem">
                {verifiedChannels.map((vc, idx) => (
                  <InfringingChannelRow
                    // eslint-disable-next-line react/no-array-index-key
                    key={vc.channelId || idx}
                    vc={vc}
                    idx={idx}
                    page={page}
                    onRemove={onRemoveChannel ? handleRemoveChannel : null}
                    removing={removing}
                  />
                ))}
              </Box>
            )}
          </Box>
        </Box>

        {/* Footer */}
        <Box
          direction="row"
          justify="end"
          gap="0.5rem"
          border={{
            color: page.tableBorder, size: '1px', style: 'solid', side: 'top',
          }}
          pad={{ top: '1rem' }}
        >
          {status === 'completed' && auditId && (
            <AppButton
              overrideHover
              level="tableControls"
              label={downloading ? 'Downloading…' : 'Download PDF'}
              disabled={downloading}
              color={button.primaryText}
              bgColor={page.highlight}
              onClick={handleDownloadPdf}
            />
          )}
          <AppButton
            overrideHover
            level="tableControls"
            label="Close"
            color={button.primaryText}
            bgColor={button.primaryBg}
            onClick={onClose}
          />
        </Box>
      </Box>
    </SimpleModal>
  );
}

AdminYtAuditDetailModal.propTypes = {
  small: PropTypes.bool.isRequired,
  audit: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onRemoveChannel: PropTypes.func,
};
AdminYtAuditDetailModal.defaultProps = {
  onRemoveChannel: null,
};

export default AdminYtAuditDetailModal;
